// Ask the server who this phone number or barcode belongs to, and apply the
// answer to the order.
//
// ONE CALL. Our own consumer record, every loyalty platform's answer, and a
// name to show all arrive together -- see lookupConsumerRoute for why they used
// to be two requests and what that cost.
//
// TWO ENTRY POINTS. `lookupConsumer` is the one the watchers run when something
// is typed; `refreshBalances` is the reload icon, and applies the balances
// without touching the consumer link.

const orNull = value => (value === undefined || value === null ? null : value)

const show = (points, credits) => {
  // null is "the platform did not report it", which is not the same as none. A
  // cashier told "0" will tell the customer they have nothing to spend.
  const read = value =>
    value === null || value === undefined ? '無法讀取' : value

  return `點數 ${read(points)} 購物金 ${read(credits)}`
}

// Null when the lookup could not be made at all, which is different from a
// lookup that answered "nobody".
async function ask(key, value, ctx) {
  try {
    // The route answers with the lookup itself, not a { data } envelope.
    return await ctx.api.get({
      url: '/api/route/lookupConsumer',
      search: { key, value },
    })
  } catch {
    // The sale must not be blocked by a lookup. Say so and let the staff carry
    // on without points.
    ctx.sendMessage({
      type: 'warning',
      message: '會員查詢失敗，可繼續結帳（無點數）',
    })

    return null
  }
}

// Where each watcher's value lives on the order: pageConfig watches
// `consumer.phone` and the top-level `mobileBarcode`, so the key the answer was
// asked under says which box to read.
function boxValue(doc, key) {
  if (key === 'mobileBarcode') return doc.mobileBarcode

  return doc.consumer && doc.consumer.phone
}

// An answer the form has already moved on from.
//
// NOTHING CANCELS A LOOKUP IN FLIGHT. The effects are taken with takeEvery, and
// correcting a mistyped digit is one backspace and one keystroke -- the first
// ask is usually still out when the second goes, and the network decides which
// lands last. A late answer for the old number would then name, bill and link
// the customer it belonged to against the number now in the box, which is the
// same wrong-customer failure the rest of this file guards against, arrived at
// by a race instead of by a branch.
//
// So an answer is applied only while the box it was asked about still holds
// what was asked. The watcher runs from editPathTask's afterUpdate queue, which
// drains after the store has taken the edit, so getDoc here already carries the
// value that fired this lookup.
function movedOn(ctx, key, value) {
  return boxValue(ctx.getDoc() || {}, key) !== value
}

// An answer nobody was asked for.
//
// `members` carries one entry per vendor the lookup ASKED, whatever it replied,
// so an empty list is the server saying there was nothing to look up at all: no
// number, and no consumer of ours to resolve one from. See
// consumers/entity/lookupConsumer, where that emptiness is decided.
//
// Such an answer knows nothing, and must overwrite nothing. Typing a number and
// then scanning 載具 is an ordinary way to ring up a customer who is new to us:
// the scan matches no consumer of ours, and it must leave both the name and the
// balances that the NUMBER resolved exactly where they are.
const askedNobody = members => (members || []).length === 0

// The membership snapshot, keyed by vendor, from every vendor that answered.
//
// Written through even when nothing was FOUND, so the balances go blank rather
// than keeping the previous customer's figures on screen -- and so an unrelated
// vendorId can never ride along on the order and credit the wrong person. A
// vendor that was asked and said "not a member" is exactly that case.
//
// Not written when no vendor was ASKED. Blanking there would throw away a
// membership this same order resolved moments ago, and the 折抵 boxes follow
// the vendor id in this snapshot -- so the sale loses the points with it.
function writeMembers(ctx, members) {
  if (askedNobody(members)) return

  const found = members.filter(member => member && member.status === 'found')

  const snapshot = found.reduce(
    (out, member) => ({
      ...out,
      [member.vendor]: {
        vendorId: member.vendorId,
        memberRef: orNull(member.memberRef),
        points: orNull(member.points),
        credits: orNull(member.credits),
      },
    }),
    {},
  )

  ctx.editPath({ path: ['consumer', 'members'], value: snapshot })
}

// What each vendor had to say. A failure is worth a word: the staff needs to
// know why there are no points to spend.
function announceMembers(ctx, members) {
  ;(members || []).forEach(member => {
    if (member.status === 'found') {
      ctx.sendMessage({
        type: 'success',
        message: `${member.vendor} 會員 ${show(member.points, member.credits)}`,
      })

      return
    }

    ctx.sendMessage({
      type: member.status === 'notFound' ? 'info' : 'warning',
      message: member.message,
    })
  })
}

// The name to show, written alone and written after the link.
//
// A LOOKUP THAT ASKED SOMEBODY OWNS THIS BOX, blank answers included. The box
// must never hold a name resolved for a DIFFERENT number: correcting a mistyped
// digit re-runs the lookup, and a re-run that finds nobody would otherwise leave
// the previous number's customer named on screen -- which saving then creates a
// consumer under. So an answer with no name blanks the box.
//
// The cost is that a name typed before the number is typed gets overwritten.
// That is the right way round: it is visible the moment it happens and the
// staff retypes it, where a name left over from another number is silent and
// gets stored. The form asks for the number first for the same reason.
//
// A LOOKUP THAT ASKED NOBODY SAYS NOTHING -- see askedNobody, which the
// membership snapshot is held to as well.
//
// ITS OWN EDIT, AND AFTER THE LINK, or the name never survives being written.
// Linking a consumer changes `consumer.id`, and pageConfig's autofill answers
// that by refilling name and phone off the record just linked -- which is
// merged over whatever the same edit carried. A platform name written alongside
// the id is therefore overwritten by the blank one it exists to stand in for. By
// this call `consumer.id` is already what it will be, the autofill has nothing
// to say, and this value stays on screen.
function writeName(ctx, result) {
  const { members, name } = result

  if (askedNobody(members)) return

  ctx.editPath({ path: ['consumer', 'name'], value: orNull(name) })
}

// Our own record, and what to call the person.
//
// `name` may have come from a platform rather than from us -- it is for display
// and is not written back to the consumer, so an order carries whatever the
// staff sees. See consumers/entity/findMemberName.
function writeConsumer(ctx, result, key, value) {
  const { consumer } = result

  // Two consumers on one number is a state a human resolves, so no link is
  // made. The name still is: what comes back is the platform's answer for the
  // number that was just typed, which is exactly what the box is for.
  if (consumer.status === 'ambiguous') {
    ctx.sendMessage({ type: 'error', message: '找到不只一位消費者' })
  } else if (consumer.status === 'notFound') {
    ctx.sendMessage({
      type: 'info',
      message: `${key}: ${value} 查無消費者：儲存後新增`,
    })
    ctx.mergePath({ path: ['consumer'], value: { id: null } })
  } else {
    const doc = consumer.doc
    ctx.sendMessage({ type: 'success', message: `連結消費者：${doc.phone}` })

    ctx.editPath({ path: ['consumer'], value: { id: doc, ...doc } })
    ctx.editPath({ path: ['mobileBarcode'], value: doc.mobileBarcode })
  }

  writeName(ctx, result)
}

// `linkConsumer` is false once an order already names one: the link is made
// once and the balances are re-read every time, which is the whole difference
// between the two halves of this answer.
async function lookupConsumer({ key, value, linkConsumer = true }, ctx) {
  const result = await ask(key, value, ctx)
  if (!result) return
  if (movedOn(ctx, key, value)) return

  if (linkConsumer) writeConsumer(ctx, result, key, value)

  writeMembers(ctx, result.members)
  announceMembers(ctx, result.members)
}

// What the reload icon runs. IT REFRESHES THE SNAPSHOT ONLY, which is safe
// precisely because nothing reads the snapshot back to decide anything.
//
// The awards already on an order are not touched and cannot be: they froze
// their own vendor and vendorId at the moment they were created. Re-resolving a
// phone that has since come to match a different member therefore cannot move
// an order already in flight onto someone else's account.
async function refreshBalances(ctx) {
  const doc = ctx.getDoc() || {}
  const phone = doc.consumer && doc.consumer.phone

  if (!phone) {
    ctx.sendMessage({ type: 'info', message: '請先輸入手機號碼' })

    return
  }

  const result = await ask('phone', phone, ctx)
  if (!result) return
  if (movedOn(ctx, 'phone', phone)) return

  writeMembers(ctx, result.members)
  announceMembers(ctx, result.members)
}

export default lookupConsumer

export { refreshBalances }
